import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { 
  Target, 
  Calendar, 
  User,
  ChevronRight,
  ChevronDown,
  Plus,
  Edit2,
  Trash2,
  Settings,
  MessageSquare,
  StickyNote,
  X
} from 'lucide-react';
import { DEFAULT_TOPICS } from './goalsDefaults';
import { ProgressRing, StatusIndicator, TrafficLight, formatDate, isOverdue, generateId } from './GoalsComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GoalsTracker = ({ goals: legacyGoals, onDelete, onRefresh, className }) => {
  // Start with empty array - will load from database or localStorage
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [expandedTopics, setExpandedTopics] = useState({});
  
  // Dialog states
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [initiativeDialogOpen, setInitiativeDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  
  // Editing states
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingInitiative, setEditingInitiative] = useState(null);
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [viewingNotes, setViewingNotes] = useState(null);
  
  // Form states
  const [goalForm, setGoalForm] = useState({
    name: '',
    description: '',
    lead: '',
    dueDate: '',
    color: '#3B82F6'
  });
  
  const [initiativeForm, setInitiativeForm] = useState({
    title: '',
    phase: 'Phase 1',
    dueDate: '',
    notes: ''
  });

  // Load goals from backend on mount
  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const response = await fetch(`${API_URL}/api/goals2026`);
        const data = await response.json();
        
        if (data.topics && data.topics.length > 0) {
          // Use saved goals from database
          setTopics(data.topics);
          // Also sync to localStorage for the Executive Overview widget
          localStorage.setItem('it_goals_2026', JSON.stringify(data.topics));
        } else if (data.source === 'empty') {
          // Database is empty - check localStorage as backup
          const localSaved = localStorage.getItem('it_goals_2026');
          if (localSaved) {
            try {
              const localTopics = JSON.parse(localSaved);
              if (localTopics && localTopics.length > 0) {
                setTopics(localTopics);
                // Save to database so it persists
                await fetch(`${API_URL}/api/goals2026`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ topics: localTopics })
                });
              } else {
                // No local data either, use defaults for first time setup
                setTopics(DEFAULT_TOPICS);
              }
            } catch {
              setTopics(DEFAULT_TOPICS);
            }
          } else {
            // First time ever - use defaults
            setTopics(DEFAULT_TOPICS);
          }
        }
        // If data.source is 'database' but topics is empty, keep current state
      } catch (error) {
        console.error('Failed to fetch goals:', error);
        // On network error, try localStorage
        const localSaved = localStorage.getItem('it_goals_2026');
        if (localSaved) {
          try {
            const localTopics = JSON.parse(localSaved);
            if (localTopics && localTopics.length > 0) {
              setTopics(localTopics);
            }
          } catch {
            // Keep current state, don't reset
          }
        }
        // Don't reset to defaults on error - keep whatever we have
      } finally {
        setLoading(false);
      }
    };
    
    fetchGoals();
  }, []);

  // Save to backend whenever topics change (debounced)
  useEffect(() => {
    if (loading) return; // Don't save while loading
    if (!topics || topics.length === 0) return; // Don't save empty goals
    
    const saveTimeout = setTimeout(async () => {
      setSaving(true);
      try {
        const response = await fetch(`${API_URL}/api/goals2026`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ topics })
        });
        
        if (response.ok) {
          // Also update localStorage for the Executive Overview widget
          localStorage.setItem('it_goals_2026', JSON.stringify(topics));
        } else {
          console.error('Failed to save goals:', response.status);
          toast.error('Failed to save goals to server');
        }
      } catch (error) {
        console.error('Failed to save goals:', error);
        // Still save to localStorage as backup
        localStorage.setItem('it_goals_2026', JSON.stringify(topics));
        toast.error('Failed to save goals to server - saved locally');
      } finally {
        setSaving(false);
      }
    }, 1000); // Debounce 1 second
    
    return () => clearTimeout(saveTimeout);
  }, [topics, loading]);

  // Calculate topic progress
  const calculateTopicProgress = (topic) => {
    if (!topic.initiatives || topic.initiatives.length === 0) return 0;
    const completed = topic.initiatives.filter(i => i.status === 'complete').length;
    return Math.round((completed / topic.initiatives.length) * 100);
  };

  // Calculate overall progress
  const calculateOverallProgress = () => {
    const totalInitiatives = topics.reduce((sum, t) => sum + (t.initiatives?.length || 0), 0);
    const completedInitiatives = topics.reduce(
      (sum, t) => sum + (t.initiatives?.filter(i => i.status === 'complete').length || 0),
      0
    );
    return totalInitiatives > 0 ? Math.round((completedInitiatives / totalInitiatives) * 100) : 0;
  };

  // Toggle initiative status
  const toggleInitiativeStatus = (topicId, initiativeId) => {
    setTopics(prevTopics => 
      prevTopics.map(topic => {
        if (topic.id !== topicId) return topic;
        return {
          ...topic,
          initiatives: topic.initiatives.map(init => {
            if (init.id !== initiativeId) return init;
            const nextStatus = {
              'not_started': 'in_progress',
              'in_progress': 'complete',
              'complete': 'not_started'
            };
            return { ...init, status: nextStatus[init.status] || 'not_started' };
          })
        };
      })
    );
    toast.success('Initiative status updated');
  };

  // Mark initiative complete directly
  const markComplete = (topicId, initiativeId, complete) => {
    setTopics(prevTopics => 
      prevTopics.map(topic => {
        if (topic.id !== topicId) return topic;
        return {
          ...topic,
          initiatives: topic.initiatives.map(init => {
            if (init.id !== initiativeId) return init;
            return { ...init, status: complete ? 'complete' : 'not_started' };
          })
        };
      })
    );
  };

  // Toggle topic expansion
  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  // ========== GOAL (TOPIC) CRUD ==========
  const openAddGoalDialog = () => {
    setEditingGoal(null);
    setGoalForm({
      name: '',
      description: '',
      lead: '',
      dueDate: '2026-12-31',
      color: '#3B82F6'
    });
    setGoalDialogOpen(true);
  };

  const openEditGoalDialog = (topic) => {
    setEditingGoal(topic);
    setGoalForm({
      name: topic.name,
      description: topic.description,
      lead: topic.lead,
      dueDate: topic.dueDate || '2026-12-31',
      color: topic.color
    });
    setGoalDialogOpen(true);
  };

  const saveGoal = () => {
    if (!goalForm.name.trim()) {
      toast.error('Goal name is required');
      return;
    }

    if (editingGoal) {
      // Update existing
      setTopics(prev => prev.map(t => 
        t.id === editingGoal.id 
          ? { ...t, ...goalForm }
          : t
      ));
      toast.success('Goal updated');
    } else {
      // Add new
      const newGoal = {
        id: generateId(),
        ...goalForm,
        initiatives: []
      };
      setTopics(prev => [...prev, newGoal]);
      toast.success('Goal added');
    }
    setGoalDialogOpen(false);
  };

  const deleteGoal = (topicId) => {
    if (confirm('Delete this goal and all its initiatives?')) {
      setTopics(prev => prev.filter(t => t.id !== topicId));
      toast.success('Goal deleted');
    }
  };

  // ========== INITIATIVE CRUD ==========
  const openAddInitiativeDialog = (topicId) => {
    setEditingTopicId(topicId);
    setEditingInitiative(null);
    setInitiativeForm({
      title: '',
      phase: 'Phase 1',
      dueDate: '2026-06-30',
      notes: ''
    });
    setInitiativeDialogOpen(true);
  };

  const openEditInitiativeDialog = (topicId, initiative) => {
    setEditingTopicId(topicId);
    setEditingInitiative(initiative);
    setInitiativeForm({
      title: initiative.title,
      phase: initiative.phase,
      dueDate: initiative.dueDate || '',
      notes: initiative.notes || ''
    });
    setInitiativeDialogOpen(true);
  };

  const saveInitiative = () => {
    if (!initiativeForm.title.trim()) {
      toast.error('Initiative title is required');
      return;
    }

    setTopics(prev => prev.map(topic => {
      if (topic.id !== editingTopicId) return topic;
      
      if (editingInitiative) {
        // Update existing
        return {
          ...topic,
          initiatives: topic.initiatives.map(init =>
            init.id === editingInitiative.id
              ? { ...init, ...initiativeForm }
              : init
          )
        };
      } else {
        // Add new
        const newInitiative = {
          id: generateId(),
          ...initiativeForm,
          status: 'not_started'
        };
        return {
          ...topic,
          initiatives: [...(topic.initiatives || []), newInitiative]
        };
      }
    }));

    toast.success(editingInitiative ? 'Initiative updated' : 'Initiative added');
    setInitiativeDialogOpen(false);
  };

  const deleteInitiative = (topicId, initiativeId) => {
    setTopics(prev => prev.map(topic => {
      if (topic.id !== topicId) return topic;
      return {
        ...topic,
        initiatives: topic.initiatives.filter(i => i.id !== initiativeId)
      };
    }));
    toast.success('Initiative deleted');
  };

  // ========== NOTES ==========
  const openNotesDialog = (topicId, initiative) => {
    setEditingTopicId(topicId);
    setViewingNotes(initiative);
    setNotesDialogOpen(true);
  };

  const saveNotes = (notes) => {
    setTopics(prev => prev.map(topic => {
      if (topic.id !== editingTopicId) return topic;
      return {
        ...topic,
        initiatives: topic.initiatives.map(init =>
          init.id === viewingNotes.id
            ? { ...init, notes }
            : init
        )
      };
    }));
    toast.success('Notes saved');
    setNotesDialogOpen(false);
  };

  // Sort topics by progress (highest first)
  const sortedTopics = [...topics].sort((a, b) => 
    calculateTopicProgress(b) - calculateTopicProgress(a)
  );

  const overallProgress = calculateOverallProgress();
  const totalInitiatives = topics.reduce((sum, t) => sum + (t.initiatives?.length || 0), 0);
  const completedInitiatives = topics.reduce(
    (sum, t) => sum + (t.initiatives?.filter(i => i.status === 'complete').length || 0),
    0
  );

  const colorOptions = [
    '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EC4899', '#06B6D4', 
    '#EF4444', '#84CC16', '#F97316', '#6366F1'
  ];

  return (
    <div className={cn('space-y-6', className)} data-testid="goals-tracker">
      {/* Loading State */}
      {loading ? (
        <Card className="p-12 bg-card/80 backdrop-blur-sm border-border/50">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground">Loading goals...</p>
          </div>
        </Card>
      ) : (
      <>
      {/* Strategic Overview Card */}
      <Card className="p-6 bg-card/80 backdrop-blur-sm border-border/50">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Target className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                2026 IT Goals Progress
              </h3>
              <p className="text-xs text-muted-foreground">
                {completedInitiatives} of {totalInitiatives} initiatives complete
                {saving && <span className="ml-2 text-primary">(Saving...)</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button size="sm" onClick={openAddGoalDialog} className="gap-2" data-testid="add-goal-btn">
              <Plus className="w-4 h-4" />
              Add Goal
            </Button>
            <ProgressRing 
              percent={overallProgress} 
              size={70} 
              strokeWidth={6}
              color={overallProgress >= 70 ? 'rgb(var(--success))' : overallProgress >= 40 ? 'rgb(var(--warning))' : 'rgb(var(--destructive))'}
            />
          </div>
        </div>

        {/* Horizontal Progress Bars by Topic */}
        <div className="space-y-4">
          {sortedTopics.map((topic) => {
            const progress = calculateTopicProgress(topic);
            return (
              <div key={topic.id} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium truncate max-w-[50%]">{topic.name}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      Due: {formatDate(topic.dueDate)}
                    </span>
                    <TrafficLight percent={progress} dueDate={topic.dueDate} initiatives={topic.initiatives} />
                    <span className="font-mono text-muted-foreground w-12 text-right">{progress}%</span>
                  </div>
                </div>
                <div className="h-3 bg-muted/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${progress}%`,
                      backgroundColor: topic.color
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Topic Drilldowns */}
      <div className="space-y-3">
        {topics.map((topic) => {
          const progress = calculateTopicProgress(topic);
          const isExpanded = expandedTopics[topic.id];
          
          return (
            <Card 
              key={topic.id} 
              className="bg-card/80 backdrop-blur-sm border-border/50 overflow-hidden"
              data-testid={`topic-${topic.id}`}
            >
              <Collapsible open={isExpanded} onOpenChange={() => toggleTopic(topic.id)}>
                <CollapsibleTrigger className="w-full">
                  <div className="p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors cursor-pointer">
                    {/* Progress Ring */}
                    <ProgressRing 
                      percent={progress} 
                      size={50} 
                      strokeWidth={5}
                      color={topic.color}
                    />
                    
                    {/* Topic Info */}
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                          {topic.name}
                        </h4>
                        <TrafficLight percent={progress} dueDate={topic.dueDate} initiatives={topic.initiatives} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span>{topic.initiatives?.filter(i => i.status === 'complete').length || 0} / {topic.initiatives?.length || 0} initiatives</span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {topic.lead}
                        </span>
                        <span>•</span>
                        <span className={cn(
                          "flex items-center gap-1",
                          isOverdue(topic.dueDate, null) && progress < 100 && "text-[rgb(var(--destructive))]"
                        )}>
                          <Calendar className="w-3 h-3" />
                          {formatDate(topic.dueDate)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Expand Icon */}
                    <div className="text-muted-foreground">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </div>
                  </div>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="px-4 pb-4 border-t border-border/50">
                    <div className="flex items-center justify-between py-3">
                      <p className="text-sm text-muted-foreground flex-1">
                        {topic.description}
                      </p>
                      <div className="flex items-center gap-2 ml-4">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); openEditGoalDialog(topic); }}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); deleteGoal(topic.id); }}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Add Initiative Button */}
                    <div className="mb-3">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => openAddInitiativeDialog(topic.id)}
                        className="gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Initiative
                      </Button>
                    </div>
                    
                    {/* Initiatives Table */}
                    <div className="space-y-2">
                      {topic.initiatives?.map((initiative) => (
                        <div 
                          key={initiative.id}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-lg border transition-all",
                            initiative.status === 'complete' 
                              ? "bg-[rgb(var(--success))]/5 border-[rgb(var(--success))]/20" 
                              : initiative.status === 'in_progress'
                              ? "bg-[rgb(var(--warning))]/5 border-[rgb(var(--warning))]/20"
                              : "bg-muted/20 border-border/30"
                          )}
                          data-testid={`initiative-${initiative.id}`}
                        >
                          {/* Checkbox */}
                          <Checkbox
                            checked={initiative.status === 'complete'}
                            onCheckedChange={(checked) => markComplete(topic.id, initiative.id, checked)}
                            className="data-[state=checked]:bg-[rgb(var(--success))] data-[state=checked]:border-[rgb(var(--success))]"
                          />
                          
                          {/* Initiative Info */}
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              "text-sm",
                              initiative.status === 'complete' && "line-through text-muted-foreground"
                            )}>
                              {initiative.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={cn(
                                "text-xs",
                                isOverdue(initiative.dueDate, initiative.status) 
                                  ? "text-[rgb(var(--destructive))]" 
                                  : "text-muted-foreground"
                              )}>
                                <Calendar className="w-3 h-3 inline mr-1" />
                                {formatDate(initiative.dueDate)}
                              </span>
                              {initiative.notes && (
                                <span className="text-xs text-primary">
                                  <StickyNote className="w-3 h-3 inline" />
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Phase Badge */}
                          <Badge variant="outline" className="text-xs shrink-0">
                            {initiative.phase}
                          </Badge>
                          
                          {/* Notes Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openNotesDialog(topic.id, initiative)}
                            className="shrink-0"
                            title="View/Add Notes"
                          >
                            <MessageSquare className={cn(
                              "w-4 h-4",
                              initiative.notes ? "text-primary" : "text-muted-foreground"
                            )} />
                          </Button>
                          
                          {/* Edit Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditInitiativeDialog(topic.id, initiative)}
                            className="shrink-0"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          
                          {/* Delete Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteInitiative(topic.id, initiative.id)}
                            className="shrink-0 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          
                          {/* Status Toggle Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleInitiativeStatus(topic.id, initiative.id)}
                            className="shrink-0"
                            title="Toggle Status"
                          >
                            <StatusIndicator status={initiative.status} />
                          </Button>
                        </div>
                      ))}
                      
                      {(!topic.initiatives || topic.initiatives.length === 0) && (
                        <div className="text-center py-6 text-muted-foreground">
                          <p className="text-sm">No initiatives yet</p>
                          <p className="text-xs mt-1">Click "Add Initiative" to get started</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {/* Saving indicator */}
      {saving && (
        <div className="flex items-center justify-center pt-4">
          <span className="text-xs text-muted-foreground">Saving...</span>
        </div>
      )}

      {/* ========== GOAL DIALOG ========== */}
      <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {editingGoal ? 'Edit Goal' : 'Add New Goal'}
            </DialogTitle>
            <DialogDescription>
              {editingGoal ? 'Update the goal details' : 'Create a new strategic goal for 2026'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="goal-name">Goal Name *</Label>
              <Input
                id="goal-name"
                placeholder="e.g., Strengthen Security Posture"
                value={goalForm.name}
                onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="goal-desc">Description</Label>
              <Textarea
                id="goal-desc"
                placeholder="Describe the goal and its strategic importance..."
                value={goalForm.description}
                onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                className="mt-2"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="goal-lead">Lead</Label>
                <Input
                  id="goal-lead"
                  placeholder="e.g., Tim/Dan"
                  value={goalForm.lead}
                  onChange={(e) => setGoalForm({ ...goalForm, lead: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="goal-due">Due Date</Label>
                <Input
                  id="goal-due"
                  type="date"
                  value={goalForm.dueDate}
                  onChange={(e) => setGoalForm({ ...goalForm, dueDate: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      "w-8 h-8 rounded-full border-2 transition-all",
                      goalForm.color === color ? "border-foreground scale-110" : "border-transparent"
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setGoalForm({ ...goalForm, color })}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setGoalDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveGoal}>
              {editingGoal ? 'Update Goal' : 'Add Goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== INITIATIVE DIALOG ========== */}
      <Dialog open={initiativeDialogOpen} onOpenChange={setInitiativeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {editingInitiative ? 'Edit Initiative' : 'Add New Initiative'}
            </DialogTitle>
            <DialogDescription>
              {editingInitiative ? 'Update the initiative details' : 'Add a new initiative to this goal'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="init-title">Initiative Title *</Label>
              <Input
                id="init-title"
                placeholder="e.g., Implement zero-touch provisioning"
                value={initiativeForm.title}
                onChange={(e) => setInitiativeForm({ ...initiativeForm, title: e.target.value })}
                className="mt-2"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="init-phase">Phase</Label>
                <Input
                  id="init-phase"
                  placeholder="e.g., Phase 1"
                  value={initiativeForm.phase}
                  onChange={(e) => setInitiativeForm({ ...initiativeForm, phase: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="init-due">Due Date</Label>
                <Input
                  id="init-due"
                  type="date"
                  value={initiativeForm.dueDate}
                  onChange={(e) => setInitiativeForm({ ...initiativeForm, dueDate: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="init-notes">Notes</Label>
              <Textarea
                id="init-notes"
                placeholder="Add any notes, blockers, or updates..."
                value={initiativeForm.notes}
                onChange={(e) => setInitiativeForm({ ...initiativeForm, notes: e.target.value })}
                className="mt-2"
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInitiativeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveInitiative}>
              {editingInitiative ? 'Update Initiative' : 'Add Initiative'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== NOTES DIALOG ========== */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              Initiative Notes
            </DialogTitle>
            <DialogDescription>
              {viewingNotes?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Textarea
              placeholder="Add notes, blockers, updates, or any relevant information..."
              value={viewingNotes?.notes || ''}
              onChange={(e) => setViewingNotes({ ...viewingNotes, notes: e.target.value })}
              className="min-h-[200px]"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => saveNotes(viewingNotes?.notes || '')}>
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
};
